import { useState } from "react";
import { useCreateCashOut } from "../hooks/useWallets";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNotification } from "@/shared/hooks/useNotification";
import { useConfirm } from "@/shared/contexts/confirm-context";

interface Props {
  customerId: string;
  spendableBalance: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CashOutDialog = ({ customerId, spendableBalance, open, onOpenChange }: Props) => {
  const notification = useNotification();
  const confirm = useConfirm();
  const mutation = useCreateCashOut();
  const [amount, setAmount] = useState("");
  const [channel, setChannel] = useState<"Cash" | "MPesa" | "BankTransfer">("MPesa");
  const [notes, setNotes] = useState("");

  const reset = () => {
    setAmount("");
    setChannel("MPesa");
    setNotes("");
  };

  const submit = () => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      notification.error("Amount must be greater than zero.");
      return;
    }
    if (value > spendableBalance) {
      notification.error(`Amount exceeds spendable balance (KES ${spendableBalance.toFixed(2)}).`);
      return;
    }

    confirm({
      title: "Submit cash-out?",
      description: `A ${channel} cash-out request will be submitted for finance to disburse.`,
      variant: "destructive",
      confirmLabel: "Submit cash-out",
      body: (
        <div className="space-y-1">
          <p className="flex justify-between">
            <span className="text-muted-foreground">Amount</span>
            <span className="font-medium">KES {value.toLocaleString()}</span>
          </p>
          <p className="flex justify-between">
            <span className="text-muted-foreground">Channel</span>
            <span className="font-medium">{channel}</span>
          </p>
        </div>
      ),
      onConfirm: () =>
        mutation
          .mutateAsync({ customerId, amount: value, channel, notes: notes.trim() || null })
          .then(() => {
            notification.success(
              "Cash-out request submitted. Finance will disburse and confirm."
            );
            reset();
            onOpenChange(false);
          })
          .catch((e) => {
            notification.error("Failed to submit cash-out request.");
            throw e;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cash out</DialogTitle>
          <DialogDescription>
            Finance will disburse via your chosen channel and paste the reference at approval.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border bg-muted/30 p-2.5 text-sm">
            <span className="text-muted-foreground">Spendable:</span>{" "}
            <span className="font-semibold">KES {spendableBalance.toFixed(2)}</span>
          </div>

          <Field>
            <FieldLabel>Amount (KES) <span className="text-destructive">*</span></FieldLabel>
            <Input
              type="number"
              step="0.01"
              min={0}
              max={spendableBalance}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={mutation.isPending}
            />
          </Field>

          <Field>
            <FieldLabel>Channel <span className="text-destructive">*</span></FieldLabel>
            <Select
              value={channel}
              onValueChange={(v) => setChannel(v as typeof channel)}
              disabled={mutation.isPending}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Cash">Cash</SelectItem>
                <SelectItem value="MPesa">M-Pesa</SelectItem>
                <SelectItem value="BankTransfer">Bank transfer</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel>Notes</FieldLabel>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} disabled={mutation.isPending} />
          </Field>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={mutation.isPending}>
            {mutation.isPending ? "Submitting..." : "Submit cash-out"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
